import { Formik, Form, Field } from 'formik';
import moment from 'moment';
import type React from 'react';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { DATE_RANGE_PRESET_LABELS } from '@frontend/api/src/notifications/constants';
import {
  Button,
  Flex,
  Popover,
  Portal,
  PopoverContent,
  PopoverFooter,
  PopoverBody,
  FormControl,
  FormLabel,
  PopoverButtonGroup,
} from '@frontend/legacy-components';

import useGetActiveUserGroupMembers from 'modules/common/userGroupManagement/switchUserGroup/hooks/useGetActiveUserGroupMembers';

import { getUser } from 'store/session/selectors';

import type { DateRangeType, FormikFieldProps, OrgsType } from '../types';
import type { FiltersType } from '../types';
import DateRangeSelector, {
  getTimeRangeFromFilters,
} from './DateRangeSelector';
import OrganizationSelector from './OrganizationSelector';

// A small hack to get the date picker to render properly.
const PopoverContainer = styled.div`
  & div.chakra-popover__popper {
    z-index: 1000;
  }
`;

type FiltersPopoverPropTypes = {
  isOpen: boolean;
  onClose: () => void;
  filters: FiltersType;
  setFilters: (arg0: FiltersType) => void;
  children?: React.ReactNode;
};

/**
 * Helper function to return date range data. Significance being whether the client is using generic filter range (e.g., "Last 7 days" ) or custom
 */
const getInitialDateRange = (filters: FiltersType): DateRangeType => {
  if (filters.dateRange) {
    return {
      label: DATE_RANGE_PRESET_LABELS[filters.dateRange],
      value: filters.dateRange,
    };
  } else {
    return filters.dateMin || filters.dateMax
      ? {
          startDate: filters.dateMin ? moment(filters.dateMin) : null,
          endDate: filters.dateMax ? moment(filters.dateMax) : null,
        }
      : null;
  }
};

const FiltersPopover: React.FC<FiltersPopoverPropTypes> = ({
  isOpen,
  onClose,
  filters,
  setFilters,
  children,
}) => {
  const containerRef = useRef();
  const { data: userGroupMembers } = useGetActiveUserGroupMembers();
  const isSuperuser = useSelector((state) => getUser(state)?.isSuperuser);

  const showOrganizationFilter =
    !isSuperuser &&
    Array.isArray(userGroupMembers) &&
    userGroupMembers.length >= 2;

  // This prevents the user from getting stuck in a weird state because there is an organization filter but it cannot be cleared through the UI
  if (!showOrganizationFilter && filters.organizationIds) {
    setFilters({
      ...filters,
      organizationIds: undefined,
    });
  }

  return (
    <>
      <Portal>
        <PopoverContainer id="popoverTarget" ref={containerRef} />
      </Portal>
      <Popover
        onClose={onClose}
        isOpen={isOpen}
        autoFocus
        size="md"
        closeOnBlur={true}
      >
        {children}
        <Portal containerRef={containerRef}>
          <PopoverContent width="500px" maxW="100vw">
            <Formik
              onSubmit={(values) => {
                const orgs = values['orgs'];
                const dateRange = getTimeRangeFromFilters(values['dateRange']);
                if (typeof dateRange === 'string') {
                  setFilters({
                    ...filters,
                    page: 1, // reset page
                    organizationIds: orgs || undefined,
                    dateMin: undefined,
                    dateMax: undefined,
                    dateRange: dateRange,
                  });
                } else {
                  setFilters({
                    ...filters,
                    page: 1, // reset page
                    organizationIds: orgs || undefined,
                    dateMin: dateRange?.startDate?.toISOString() || undefined,
                    dateMax: dateRange?.endDate?.toISOString() || undefined,
                    dateRange: undefined,
                  });
                }
                onClose();
              }}
              initialValues={{
                orgs: filters.organizationIds,
                dateRange: getInitialDateRange(filters),
              }}
              enableReinitialize
            >
              {() => (
                <Form>
                  <PopoverBody padding={0}>
                    {showOrganizationFilter ? (
                      <Field name="orgs">
                        {({ field, form }: FormikFieldProps<OrgsType>) => {
                          return (
                            <FormControl
                              display="flex"
                              gap={1.5}
                              alignItems="center"
                              maxW="100%"
                              paddingTop={1.5}
                              paddingBottom={0.75}
                              paddingRight={1.5}
                              paddingLeft={1.5}
                            >
                              <Flex flex="0 0 80px" justifyContent="right">
                                <FormLabel
                                  mt="auto"
                                  mb="auto"
                                  height="initial"
                                  htmlFor="orgs"
                                >
                                  Organization
                                </FormLabel>
                              </Flex>
                              <OrganizationSelector
                                id="orgs"
                                orgIds={field.value}
                                onChange={(values) => {
                                  form.setFieldValue('orgs', values);
                                }}
                                userGroupMembers={userGroupMembers}
                              />
                            </FormControl>
                          );
                        }}
                      </Field>
                    ) : null}
                    <Field name="dateRange">
                      {({ field, form }: FormikFieldProps<DateRangeType>) => {
                        return (
                          <FormControl
                            display="flex"
                            gap={showOrganizationFilter ? 3.5 : 1.5}
                            alignItems="center"
                            maxW="100%"
                            paddingTop={showOrganizationFilter ? 0.75 : 1.5}
                            paddingBottom={1.5}
                            paddingRight={1.5}
                            paddingLeft={1.5}
                          >
                            <Flex flex={'0 0 80px'} justifyContent="right">
                              <FormLabel
                                mt="auto"
                                mb="auto"
                                height="initial"
                                htmlFor="dateRange"
                              >
                                Date Range
                              </FormLabel>
                            </Flex>
                            <DateRangeSelector
                              value={field.value}
                              onChange={(value) => {
                                form.setFieldValue('dateRange', value);
                              }}
                            />
                          </FormControl>
                        );
                      }}
                    </Field>
                    <PopoverFooter>
                      <PopoverButtonGroup>
                        <Button variant="secondary" onClick={onClose}>
                          Cancel
                        </Button>
                        <Button variant="primary" type="submit">
                          Apply Filters
                        </Button>
                      </PopoverButtonGroup>
                    </PopoverFooter>
                  </PopoverBody>
                </Form>
              )}
            </Formik>
          </PopoverContent>
        </Portal>
      </Popover>
    </>
  );
};

export default FiltersPopover;
